import { useEffect, useState } from 'react';

/**
 * Coordinates when the splash screen dismisses.
 *
 * The splash hides only when BOTH conditions are met:
 *   1. A minimum display time has elapsed (so it never flickers past too fast
 *      to read — a sub-second splash looks like a bug, not a brand moment).
 *   2. The app reports it is ready (`ready === true`) — e.g. auth resolved,
 *      first data loaded, fonts in. Pass whatever readiness means for you.
 *
 * Pass `ready` from your app. If you don't have a real readiness signal yet,
 * pass `true` and the splash will simply dismiss after `minDurationMs`.
 *
 * Returns:
 *   - `visible`  — render the <Splash> while true.
 *   - `leaving`  — true once dismissal starts; drive the fade-OUT with it,
 *                  then call `onExited` when the CSS transition ends.
 *
 * Lifecycle:  visible+!leaving (showing)  →  visible+leaving (fading out)
 *             →  onExited()  →  !visible (unmounted)
 */
export interface UseSplashOptions {
  /** App readiness signal. Splash won't dismiss until this is true. */
  ready: boolean;
  /** Minimum time the splash stays up, in ms. Default 1500. */
  minDurationMs?: number;
}

export interface UseSplashResult {
  visible: boolean;
  leaving: boolean;
  /** Call when the fade-out transition has finished. */
  onExited: () => void;
}

export function useSplash({ ready, minDurationMs = 1500 }: UseSplashOptions): UseSplashResult {
  const [minElapsed, setMinElapsed] = useState(false);
  const [visible, setVisible] = useState(true);

  // Start the minimum-duration timer once, on mount.
  useEffect(() => {
    const id = window.setTimeout(() => setMinElapsed(true), minDurationMs);
    return () => window.clearTimeout(id);
  }, [minDurationMs]);

  // Derived, not stored: leaving begins once BOTH gates are open. Both minElapsed
  // and ready only ever transition false→true, so this is monotonic — no effect or
  // setState-in-effect needed (which the react-hooks lint rule flags).
  const leaving = minElapsed && ready;

  const onExited = () => setVisible(false);

  return { visible, leaving, onExited };
}